import React, { useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { IconS24Close24, IconS24Id24, IconS24Mobile24, IconS24ShieldCheckmark24 } from '@is24/cosma-ui-icons';
import { Loader } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { getWebIdSessionVerifiedAt } from '../../api/webid/webIdVerification';
import { VerificationStatus } from '../../@types/VerificationStatus';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useTvsState } from '../../utils/useTvsState';
import { trackEvent } from '../../tracking/tracking';
import webIdLogo from '../../components/webIdVerification/webid-logo.svg';
import './WebIdDesktopHandoffPage.css';

const POLLING_INTERVAL_MS = 2000;
const TVS_PROJECTION_DELAY_MS = 5000;

interface WebIdDesktopHandoffPageProps {
  redirectUrl: string;
  sessionId: string;
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const WebIdDesktopHandoffPage: React.FC<WebIdDesktopHandoffPageProps> = ({
  redirectUrl,
  sessionId,
  onVerificationComplete,
}) => {
  const { t } = useTranslation();
  const { clientId } = useClientConfig();
  const [, setTvsState] = useTvsState();

  useEffect(() => {
    let stopped = false;
    let completionTimer: number | undefined;

    const complete = () => {
      if (stopped) return;
      stopped = true;
      window.clearInterval(interval);
      completionTimer = window.setTimeout(() => {
        trackEvent(
          'is24.de.sicherheit.verifizierung.webid',
          'verification_successful',
          'user',
          clientId,
          'webid',
        ).catch();
        setTvsState('verification_success');
        onVerificationComplete?.(VerificationStatus.SUCCESS);
      }, TVS_PROJECTION_DELAY_MS);
    };

    const fail = () => {
      if (stopped) return;
      stopped = true;
      window.clearInterval(interval);
      setTvsState('verification_failure_webid');
    };

    const checkStatus = async () => {
      try {
        const response = await getWebIdSessionVerifiedAt(sessionId);
        if (response.verifiedAt) complete();
        if (response.status === 'DECLINED' || response.status === 'EXPIRED') fail();
      } catch {
        // A temporary status-check failure must not interrupt the handoff.
      }
    };

    checkStatus();
    const interval = window.setInterval(checkStatus, POLLING_INTERVAL_MS);
    return () => {
      stopped = true;
      window.clearInterval(interval);
      if (completionTimer) window.clearTimeout(completionTimer);
    };
  }, [clientId, onVerificationComplete, sessionId, setTvsState]);

  return (
    <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
      <div className='modal-container webid-desktop-handoff-modal'>
        <div className='webid-desktop-handoff-header'>
          <img
            className='webid-desktop-handoff-logo'
            src={webIdLogo}
            alt={t('WebIdVerificationStartPage.providerName')}
          />
          <IconS24Close24 onClick={() => setTvsState('verification_failure_webid')} data-testid='modal-close-icon' />
        </div>
        <div className='modal-content-wrapper'>
          <div className='modal-content webid-desktop-handoff-content'>
            <h2 className='font-heading-small-bold webid-desktop-handoff-title margin-bottom-l'>
              {t('WebIdDesktopHandoffPage.heading')}
            </h2>
            <div className='webid-desktop-handoff-requirements'>
              <div className='webid-desktop-handoff-requirement'>
                <IconS24Id24 aria-hidden='true' />
                <div>
                  <strong className='font-body-regular'>{t('WebIdDesktopHandoffPage.documentTitle')}</strong>
                  <span className='font-body-small-regular'>{t('WebIdDesktopHandoffPage.documentDescription')}</span>
                </div>
              </div>
              <div className='webid-desktop-handoff-requirement'>
                <IconS24Mobile24 aria-hidden='true' />
                <div>
                  <strong className='font-body-regular'>{t('WebIdDesktopHandoffPage.phoneTitle')}</strong>
                  <span className='font-body-small-regular'>{t('WebIdDesktopHandoffPage.phoneDescription')}</span>
                </div>
              </div>
            </div>
            <div className='webid-desktop-handoff-qr-card'>
              <h3 className='font-heading-xsmall-bold margin-bottom-s'>{t('WebIdDesktopHandoffPage.qrTitle')}</h3>
              <p className='font-body-small-regular margin-bottom-l'>{t('WebIdDesktopHandoffPage.description')}</p>
              <QRCodeSVG data-testid='webid-qr-code' value={redirectUrl} size={210} level='M' includeMargin />
              <div className='webid-desktop-handoff-waiting font-body-small-regular' role='status' aria-live='polite'>
                <Loader size='small' aria-hidden='true' />
                <span>{t('WebIdDesktopHandoffPage.waiting')}</span>
              </div>
            </div>
            <div className='webid-desktop-handoff-footer font-body-small-regular'>
              <IconS24ShieldCheckmark24 aria-hidden='true' />
              <span>{t('WebIdDesktopHandoffPage.footer')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdDesktopHandoffPage;
